import React, { useMemo } from 'react';
import { Linking, StyleSheet, type StyleProp, type TextStyle } from 'react-native';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  style?: StyleProp<TextStyle>;
  isUserMessage?: boolean;
}

type NormalizedLine = {
  kind: 'blank' | 'code' | 'heading' | 'list' | 'paragraph' | 'table';
  text: string;
};

const bulletPattern = /^[•‣▪◦]\s+/;
const tableLinePattern = /^\|?.+\|.+\|?$/;
const tableSeparatorPattern = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/;
const markdownHeadingPattern = /^#{1,6}\s+/;
const reportHeadingPattern = /^(B[OÖ]L[UÜ]M\s+\d+\s*:.+|GENEL SEV[Iİ]YE\s*\(.+\)\s*:.+|SONU[CÇ]\s*:.+)$/i;
const reportLabelPattern = /^([A-ZÇĞİÖŞÜ0-9][A-ZÇĞİÖŞÜ0-9\s/().-]{1,80}:)\s*(.+)$/;
const safeLinkProtocols = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function getSafeLinkUrl(href?: string): string | undefined {
  const value = href?.trim();
  if (!value || /[\u0000-\u001f\u007f]/.test(value)) return undefined;
  if (value.startsWith('//') || value.startsWith('/\\')) return undefined;
  if (value.startsWith('#') || value.startsWith('/') || value.startsWith('./') || value.startsWith('../')) {
    return value;
  }
  try {
    const parsed = new URL(value);
    return safeLinkProtocols.has(parsed.protocol) ? value : undefined;
  } catch {
    return undefined;
  }
}

function openSafeLink(url: string) {
  if (url.startsWith('#') || url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
    window.location.assign(url);
    return;
  }
  void Linking.openURL(url);
}

function isTableLine(line: string): boolean {
  const trimmed = line.trim();
  return tableSeparatorPattern.test(trimmed) || (tableLinePattern.test(trimmed) && trimmed.split('|').length >= 3);
}

function nextNonEmptyLine(lines: string[], index: number): string {
  for (let i = index + 1; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (trimmed) return trimmed;
  }
  return '';
}

function isNumberedReportHeading(line: string, lines: string[], index: number): boolean {
  return /^\d+\.\s+\S/.test(line.trim()) && nextNonEmptyLine(lines, index).toLocaleLowerCase('tr').startsWith('seviye:');
}

function normalizeReportMarkdown(raw: string): string {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const normalized: NormalizedLine[] = [];
  let inCodeFence = false;

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('```')) {
      inCodeFence = !inCodeFence;
      normalized.push({ kind: 'code', text: line });
      return;
    }

    if (inCodeFence) {
      normalized.push({ kind: 'code', text: line });
      return;
    }

    if (!trimmed) {
      normalized.push({ kind: 'blank', text: '' });
      return;
    }

    if (isTableLine(trimmed)) {
      normalized.push({ kind: 'table', text: line });
      return;
    }

    if (markdownHeadingPattern.test(trimmed)) {
      normalized.push({ kind: 'heading', text: trimmed });
      return;
    }

    if (reportHeadingPattern.test(trimmed)) {
      const level = trimmed.toLocaleUpperCase('tr').startsWith('BOL') || trimmed.toLocaleUpperCase('tr').startsWith('BÖL')
        ? '##'
        : '###';
      normalized.push({ kind: 'heading', text: `${level} ${trimmed}` });
      return;
    }

    if (isNumberedReportHeading(trimmed, lines, index)) {
      normalized.push({ kind: 'heading', text: `### ${trimmed}` });
      return;
    }

    if (bulletPattern.test(trimmed)) {
      normalized.push({ kind: 'list', text: trimmed.replace(bulletPattern, '- ') });
      return;
    }

    if (/^([-*+]|\d+\.)\s+/.test(trimmed)) {
      normalized.push({ kind: 'list', text: trimmed });
      return;
    }

    const labelMatch = reportLabelPattern.exec(trimmed);
    if (labelMatch) {
      normalized.push({ kind: 'paragraph', text: `**${labelMatch[1]}** ${labelMatch[2]}` });
      return;
    }

    normalized.push({ kind: 'paragraph', text: trimmed });
  });

  const output: string[] = [];
  normalized.forEach((line, index) => {
    const prev = normalized[index - 1];
    const next = normalized[index + 1];

    if (line.kind === 'blank') {
      if (output[output.length - 1] !== '') output.push('');
      return;
    }

    if (line.kind === 'heading' && prev && prev.kind !== 'blank') {
      output.push('');
    }

    output.push(line.text);

    if (line.kind === 'code') return;

    const nextKind = next?.kind;
    if (!nextKind || nextKind === 'blank') return;

    const shouldSeparate =
      line.kind === 'heading'
      || (line.kind === 'paragraph' && nextKind !== 'table')
      || (line.kind === 'list' && nextKind !== 'list')
      || (line.kind === 'table' && nextKind !== 'table');

    if (shouldSeparate) output.push('');
  });

  return output.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export default function MarkdownRenderer({
  content,
  style,
  isUserMessage = false,
}: MarkdownRendererProps) {
  const flattenedStyle = useMemo(() => (StyleSheet.flatten(style) ?? {}) as TextStyle, [style]);
  const markdown = useMemo(() => normalizeReportMarkdown(content || ''), [content]);

  const colors = {
    text: isUserMessage ? '#ffffff' : (flattenedStyle.color as string | undefined) ?? '#111827',
    muted: isUserMessage ? 'rgba(255,255,255,0.76)' : '#4b5563',
    heading: isUserMessage ? '#ffffff' : '#111827',
    border: isUserMessage ? 'rgba(255,255,255,0.22)' : '#d8dee8',
    tableHeader: isUserMessage ? 'rgba(255,255,255,0.14)' : '#f3f6fc',
    tableBase: isUserMessage ? 'rgba(255,255,255,0.03)' : '#ffffff',
    tableStripe: isUserMessage ? 'rgba(255,255,255,0.06)' : '#fbfcfe',
    codeBg: isUserMessage ? 'rgba(255,255,255,0.13)' : '#f3f4f6',
    link: isUserMessage ? '#ffffff' : '#1d4ed8',
  };

  const baseFontSize = Number(flattenedStyle.fontSize ?? 15);
  const baseLineHeight = Number(flattenedStyle.lineHeight ?? 22);

  const css = `
    .mc-markdown {
      width: 100%;
      min-width: 0;
      color: ${colors.text};
      font-size: ${baseFontSize}px;
      line-height: ${baseLineHeight}px;
      user-select: text;
      -webkit-user-select: text;
      overflow-wrap: anywhere;
      word-break: normal;
      letter-spacing: 0;
      container-type: inline-size;
    }
    .mc-markdown * {
      box-sizing: border-box;
      user-select: text;
      -webkit-user-select: text;
      letter-spacing: 0;
    }
    .mc-markdown p {
      margin: 0 0 12px;
    }
    .mc-markdown h1,
    .mc-markdown h2,
    .mc-markdown h3 {
      color: ${colors.heading};
      font-weight: 800;
      margin: 18px 0 10px;
      line-height: 1.22;
    }
    .mc-markdown h1:first-child,
    .mc-markdown h2:first-child,
    .mc-markdown h3:first-child {
      margin-top: 0;
    }
    .mc-markdown h1 { font-size: ${Math.max(22, baseFontSize + 8)}px; }
    .mc-markdown h2 {
      font-size: ${Math.max(20, baseFontSize + 5)}px;
      padding-top: 4px;
    }
    .mc-markdown h3 {
      font-size: ${Math.max(17, baseFontSize + 2)}px;
      margin-top: 16px;
    }
    .mc-markdown strong {
      color: ${colors.heading};
      font-weight: 760;
    }
    .mc-markdown em {
      color: ${colors.muted};
    }
    .mc-markdown ul,
    .mc-markdown ol {
      margin: 0 0 14px;
      padding-left: 22px;
    }
    .mc-markdown li {
      margin: 0 0 8px;
      padding-left: 2px;
    }
    .mc-markdown li::marker {
      color: ${colors.muted};
      font-weight: 700;
    }
    .mc-markdown blockquote {
      margin: 12px 0;
      padding: 10px 12px;
      border-left: 4px solid ${colors.link};
      border-radius: 8px;
      background: ${isUserMessage ? 'rgba(255,255,255,0.08)' : '#f8fafc'};
      color: ${colors.muted};
    }
    .mc-markdown code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
      font-size: 0.92em;
      background: ${colors.codeBg};
      border: 1px solid ${colors.border};
      border-radius: 5px;
      padding: 1px 5px;
      white-space: break-spaces;
    }
    .mc-markdown pre {
      margin: 12px 0;
      padding: 12px;
      overflow-x: auto;
      border: 1px solid ${colors.border};
      border-radius: 8px;
      background: ${colors.codeBg};
      -webkit-overflow-scrolling: touch;
    }
    .mc-markdown pre code {
      display: block;
      border: 0;
      padding: 0;
      background: transparent;
      white-space: pre;
    }
    .mc-markdown a {
      color: ${colors.link};
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    .mc-markdown hr {
      border: 0;
      border-top: 1px solid ${colors.border};
      margin: 18px 0;
    }
    .mc-markdown .table-wrap {
      position: relative;
      width: 100%;
      max-width: 100%;
      margin: 12px 0 18px;
      overflow-x: auto;
      border: 1px solid ${colors.border};
      border-radius: 10px;
      background: ${isUserMessage ? 'rgba(255,255,255,0.06)' : '#ffffff'};
      -webkit-overflow-scrolling: touch;
      overscroll-behavior-x: contain;
      scrollbar-width: thin;
      scrollbar-color: ${colors.border} transparent;
    }
    .mc-markdown .table-wrap::-webkit-scrollbar {
      height: 10px;
    }
    .mc-markdown .table-wrap::-webkit-scrollbar-track {
      background: transparent;
    }
    .mc-markdown .table-wrap::-webkit-scrollbar-thumb {
      background: ${colors.border};
      border-radius: 999px;
      border: 3px solid transparent;
      background-clip: content-box;
    }
    .mc-markdown .table-wrap::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 24px;
      pointer-events: none;
      background: linear-gradient(90deg, transparent, ${isUserMessage ? 'rgba(30,64,175,0.22)' : 'rgba(255,255,255,0.94)'});
    }
    .mc-markdown table {
      width: max-content;
      min-width: 100%;
      max-width: none;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: auto;
      font-size: ${Math.max(13, baseFontSize - 2)}px;
      line-height: 1.45;
    }
    .mc-markdown th,
    .mc-markdown td {
      padding: 10px 12px;
      text-align: left;
      vertical-align: top;
      border-right: 1px solid ${colors.border};
      border-bottom: 1px solid ${colors.border};
      overflow-wrap: anywhere;
      word-break: normal;
      hyphens: auto;
      min-width: 132px;
      max-width: 360px;
      max-width: min(360px, 44cqw);
      background: ${colors.tableBase};
    }
    .mc-markdown th:last-child,
    .mc-markdown td:last-child {
      border-right: 0;
    }
    .mc-markdown tr:last-child td {
      border-bottom: 0;
    }
    .mc-markdown th {
      position: sticky;
      top: 0;
      z-index: 1;
      color: ${colors.heading};
      background: ${colors.tableHeader};
      font-weight: 780;
      white-space: normal;
    }
    .mc-markdown thead th:first-child,
    .mc-markdown tbody td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      box-shadow: 1px 0 0 ${colors.border}, 10px 0 14px -14px rgba(16, 24, 39, 0.34);
    }
    .mc-markdown thead th:first-child {
      z-index: 3;
      background: ${colors.tableHeader};
    }
    .mc-markdown tbody tr:nth-child(odd) td {
      background: ${colors.tableBase};
    }
    .mc-markdown tbody tr:nth-child(even) td {
      background: ${colors.tableStripe};
    }
    .mc-markdown tbody tr:nth-child(odd) td:first-child {
      background: ${colors.tableBase};
    }
    .mc-markdown tbody tr:nth-child(even) td:first-child {
      background: ${colors.tableStripe};
    }
    .mc-markdown thead th:nth-child(1),
    .mc-markdown tbody td:nth-child(1) {
      min-width: 190px;
      max-width: 300px;
      max-width: min(300px, 46cqw);
      font-weight: 650;
      color: ${colors.heading};
    }
    .mc-markdown thead th:nth-child(2),
    .mc-markdown tbody td:nth-child(2) {
      min-width: 118px;
      max-width: 180px;
    }
    .mc-markdown thead th:nth-child(3),
    .mc-markdown tbody td:nth-child(3) {
      min-width: 230px;
      max-width: 390px;
      max-width: min(390px, 54cqw);
    }
    @container (max-width: 620px) {
      .mc-markdown table {
        font-size: 12.5px;
      }
      .mc-markdown th,
      .mc-markdown td {
        padding: 8px 9px;
        min-width: 112px;
        max-width: 280px;
        max-width: min(280px, 52cqw);
      }
      .mc-markdown thead th:nth-child(1),
      .mc-markdown tbody td:nth-child(1) {
        min-width: 158px;
        max-width: 230px;
        max-width: min(230px, 56cqw);
      }
      .mc-markdown thead th:nth-child(2),
      .mc-markdown tbody td:nth-child(2) {
        min-width: 96px;
        max-width: 150px;
      }
      .mc-markdown thead th:nth-child(3),
      .mc-markdown tbody td:nth-child(3) {
        min-width: 190px;
        max-width: 280px;
        max-width: min(280px, 62cqw);
      }
    }
    @media (max-width: 640px) {
      .mc-markdown {
        font-size: ${Math.max(14, baseFontSize - 1)}px;
        line-height: ${Math.max(20, baseLineHeight - 1)}px;
      }
      .mc-markdown h2 {
        font-size: 18px;
      }
      .mc-markdown h3 {
        font-size: 16px;
      }
      .mc-markdown .table-wrap {
        margin-left: -2px;
        margin-right: -2px;
      }
      .mc-markdown table {
        font-size: 12.5px;
      }
      .mc-markdown th,
      .mc-markdown td {
        padding: 9px 10px;
      }
      .mc-markdown thead th:nth-child(1),
      .mc-markdown tbody td:nth-child(1) {
        min-width: 158px;
      }
      .mc-markdown thead th:nth-child(2),
      .mc-markdown tbody td:nth-child(2) {
        min-width: 96px;
      }
      .mc-markdown thead th:nth-child(3),
      .mc-markdown tbody td:nth-child(3) {
        min-width: 190px;
      }
    }
  `;

  if (!markdown) {
    return (
      <div className="mc-markdown" style={{ color: colors.text }}>
        <style>{css}</style>
        <p>-</p>
      </div>
    );
  }

  const components: any = {
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      const safeHref = getSafeLinkUrl(href);
      return (
      <a
        href={safeHref}
        rel="noopener noreferrer"
        onClick={(event) => {
          event.preventDefault();
          if (!safeHref) return;
          openSafeLink(safeHref);
        }}
      >
        {children}
      </a>
      );
    },
    table: ({ children }: { children?: React.ReactNode }) => (
      <div className="table-wrap">
        <table>{children}</table>
      </div>
    ),
  };

  return (
    <div
      className="mc-markdown"
      style={{
        color: colors.text,
      }}
    >
      <style>{css}</style>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
